import { useState, useRef, useEffect, useCallback } from 'react';
import Card from 'components/card';
import { MdUploadFile, MdDownload, MdDelete, MdCheckCircle, MdError, MdExpandMore, MdExpandLess, MdWarning, MdClose, MdRefresh } from 'react-icons/md';
import notificationService from '@services/notificationService';
import { processCSVDataToProfiles, batchCreateUsers } from '@services/cloudFunctions';
import { useTenant } from '@contexts/TenantContext';
import { PersonalInfo, isPersonalInfo, Education, Work, Activity, QuestionResponse } from 'types/profile.types';
import ConfirmationModal from 'components/modal/ConfirmationModal';
import {
  UserData,
  validateUserData,
  parseCSVRows,
  parseHeaderLine,
  parseCSVLine,
  findRequiredColumns
} from '@utils/csvParser';

type ProcessingPhase = 'idle' | 'parsing' | 'ai-processing' | 'creating';

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const LARGE_ROW_THRESHOLD = 50;
const CHUNK_SIZE = 100; // Matches backend limit of 100 users per call
const CHUNK_CONCURRENCY = 2; // Safe concurrency — backend processes users sequentially within each chunk
const MAX_PAYLOAD_BYTES = 9 * 1024 * 1024; // 9MB warning threshold (Firebase limit ~10MB)

const UserCard = ({ user, index, isExpanded, onToggle, onRemove }: {
  user: UserData;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
}) => {
  const hasAdditionalData = user.additionalData && Object.keys(user.additionalData).length > 0;
  const additionalDataKeys = hasAdditionalData ? Object.keys(user.additionalData) : [];

  return (
    <div className={`p-3 rounded-lg ${user.status === 'valid'
      ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
      : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {user.status === 'valid' ? (
              <MdCheckCircle className="h-4 w-4 text-green-500" />
            ) : (
              <MdError className="h-4 w-4 text-red-500" />
            )}
            <p className="font-medium text-navy-700 dark:text-white">
              {user.firstName} {user.lastName}
            </p>
            {hasAdditionalData && (
              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-1 rounded">
                {additionalDataKeys.length} data field{additionalDataKeys.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{user.email}</p>
          {!isExpanded && hasAdditionalData && (
            <div className="flex flex-wrap gap-1">
              {Object.entries(user.additionalData!).slice(0, 3).map(([key, value]) => (
                <span key={key} className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  {key}: {value.length > 20 ? `${value.substring(0, 20)}...` : value}
                </span>
              ))}
              {additionalDataKeys.length > 3 && (
                <span className="text-xs text-gray-500">+{additionalDataKeys.length - 3} more</span>
              )}
            </div>
          )}
          {user.errorMessage && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{user.errorMessage}</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {hasAdditionalData && (
            <button onClick={onToggle} className="p-1 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 rounded" title={isExpanded ? "Show less" : "Show all data"}>
              {isExpanded ? <MdExpandLess className="h-4 w-4" /> : <MdExpandMore className="h-4 w-4" />}
            </button>
          )}
          <button onClick={onRemove} className="p-1 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 rounded" title="Remove user">
            <MdDelete className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isExpanded && hasAdditionalData && (
        <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">All Data Fields:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(user.additionalData!).map(([key, value]) => (
              <div key={key} className="bg-white dark:bg-navy-900 p-2 rounded text-xs">
                <span className="font-medium text-gray-700 dark:text-gray-300">{key}:</span>
                <span className="text-gray-600 dark:text-gray-400 ml-1 break-words">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AddUserFile = () => {
  const { currentTenantId } = useTenant();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [userData, setUserData] = useState<UserData[]>([]);
  const [processedProfiles, setProcessedProfiles] = useState<PersonalInfo[]>([]);
  const [processingPhase, setProcessingPhase] = useState<ProcessingPhase>('idle');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [uploadError, setUploadError] = useState<string>('');
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [creationError, setCreationError] = useState<string>('');
  const [showCreationErrorDialog, setShowCreationErrorDialog] = useState(false);
  const [creationResults, setCreationResults] = useState<{
    successful: any[];
    existing: any[];
    failed: any[];
  } | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [showLargeFileConfirm, setShowLargeFileConfirm] = useState(false);
  const processingFileRef = useRef<string | null>(null);
  const cancelledRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Derived booleans for backward compatibility in JSX
  const isProcessing = processingPhase === 'parsing' || processingPhase === 'ai-processing';
  const isCreatingUsers = processingPhase === 'creating';

  // Elapsed timer for long-running operations
  useEffect(() => {
    if (processingPhase === 'ai-processing' || processingPhase === 'creating') {
      setElapsedSeconds(0);
      timerIntervalRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    } else {
      setElapsedSeconds(0);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [processingPhase]);

  const formatElapsed = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const estimatePayloadSize = (data: any): number => {
    try {
      return new Blob([JSON.stringify(data)]).size;
    } catch {
      return 0;
    }
  };

  const handleCancelProcessing = useCallback(() => {
    cancelledRef.current = true;
    setProcessingPhase('idle');
    notificationService.info('Processing cancelled. Results will be discarded.');
  }, []);

  const toggleUserExpand = (index: number) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      newSet.has(index) ? newSet.delete(index) : newSet.add(index);
      return newSet;
    });
  };

  const showError = (errorMessage: string) => {
    setUploadError(errorMessage);
    setShowErrorDialog(true);
  };

  // Download CSV template
  const downloadTemplate = () => {
    const template = `first_name,last_name,email,quote,professional,mentor,latitude,longitude,edu_college,edu_level,edu_majors,edu_start,edu_end,edu_desc,work_company,work_position,work_field,work_start,work_end,work_desc,activity_org,activity_position,activity_start,activity_end,activity_desc,question1,answer1,interests,links
Alice,Chen,alice@example.com,"Strive for progress, not perfection",TRUE,TRUE,43.6532,-79.3832,University of Toronto,BSc,"Computer Science;Mathematics",2016-09,2020-06,"Dean's list, student leader",Acme Inc.,Product Manager,"Product;AI",2020-07-01,2023-03-15,Led AI product team,ACM Club,Chair,2018-01,2020-06,Organized events,Why mentorship?,To give back,"AI;Mentorship;Product",https://alice.co
Bob,Smith,bob.smith@foo.io,Keep it simple,FALSE,FALSE,40.7128,-74.006,MIT,MSc,Data Science,2015-09,2017-06,Graduate research on ML,Fabrikam,Senior Engineer,"Backend;Systems",2017-07-01,,Built scalable systems,Hackathon Club,Participant,2016-09,2017-06,Joined coding events,Favorite tech?,Rust,"Engineering;Backend",https://bobsmith.dev
Sarah,Johnson,sarah.j@tech.com,"Innovation through collaboration",TRUE,TRUE,37.7749,-122.4194,Stanford University,PhD,"Artificial Intelligence;Robotics",2018-09,2023-06,Published 15 papers,Google,Research Scientist,"AI;Research",2023-07-01,,Leading ML research,Women in Tech,VP,2020-01,2023-06,Promoted diversity in STEM,Greatest achievement?,"PhD in AI at 24","Research;AI;Robotics;Leadership",https://sarahj.ai`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_bulk_creation_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Shared AI processing logic used by handleFileUpload and handleConfirmLargeFile
  const processValidUsersWithAI = async (validUsers: UserData[], validatedUsers: UserData[]) => {
    setProcessingPhase('ai-processing');
    cancelledRef.current = false;
    let processingNotificationId: string | undefined;

    try {
      // Check payload size before sending
      const payloadSize = estimatePayloadSize({ csvData: validUsers });
      if (payloadSize > MAX_PAYLOAD_BYTES) {
        showError(`The data is too large to process (${(payloadSize / 1024 / 1024).toFixed(1)}MB). Please reduce the number of rows or columns and try again.`);
        return;
      }

      processingNotificationId = notificationService.info(`Processing ${validUsers.length} users with Latte AI...`, 0);
      const result = await processCSVDataToProfiles(validUsers);

      if (cancelledRef.current) {
        if (processingNotificationId) notificationService.remove(processingNotificationId);
        return;
      }

      if (result.success && result.data) {
        const validProfiles = result.data.filter(isPersonalInfo);

        if (validProfiles.length === 0) {
          if (processingNotificationId) notificationService.remove(processingNotificationId);
          setUserData(validatedUsers);
          showError('Oops! We had trouble processing your file. Please try uploading it again.');
          return;
        }

        if (processingNotificationId) notificationService.remove(processingNotificationId);
        setProcessedProfiles(validProfiles);
        setUserData([]);
        notificationService.success(`CSV uploaded and ${validProfiles.length} profiles processed with Latte AI!`);
      } else {
        if (processingNotificationId) notificationService.remove(processingNotificationId);
        setUserData(validatedUsers);
        showError('Something went wrong while processing your file. Please try uploading it again.');
      }
    } catch (aiError: any) {
      if (processingNotificationId) notificationService.remove(processingNotificationId);
      setUserData(validatedUsers);

      const errorMessage = aiError.message?.includes('timeout') || aiError.message?.includes('network') || aiError.code === 'deadline-exceeded' || aiError.message?.includes('DEADLINE_EXCEEDED')
        ? 'The upload took too long. Please check your internet connection and try again.'
        : aiError.message?.includes('quota') || aiError.message?.includes('limit')
        ? 'We\'ve reached our processing limit. Please try again in a few minutes.'
        : aiError.code === 'PERMISSION_DENIED' || aiError.message?.includes('permission')
        ? 'The service is temporarily unavailable. Please try again in a moment.'
        : 'Something went wrong while processing your file. Please try uploading it again.';

      showError(errorMessage);
    }
  };

  // Handle CSV file upload
  const handleFileUpload = async (file: File) => {
    const fileId = `${file.name}-${file.size}-${file.lastModified}`;

    // Block concurrent uploads
    if (processingPhase !== 'idle') {
      notificationService.warning('A file is already being processed. Please wait.');
      return;
    }

    if (processingFileRef.current === fileId) return;
    processingFileRef.current = fileId;

    setUploadError('');

    // Validate file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      showError(`File is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Please upload a file under 5MB.`);
      processingFileRef.current = null;
      return;
    }

    // Validate file type
    if (!file.type.includes('csv') && !file.name.endsWith('.csv')) {
      showError('Please upload a CSV file.');
      processingFileRef.current = null;
      return;
    }

    setProcessingPhase('parsing');

    try {
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      // Parse CSV using utility function
      const lines = parseCSVRows(text);

      if (lines.length < 2) {
        showError('File must contain at least a header row and one data row');
        return;
      }

      const headers = parseHeaderLine(lines[0]).map(h => h.toLowerCase());

      // Find required column indices using utility function
      const { firstNameIndex, lastNameIndex, emailIndex } = findRequiredColumns(headers);

      if (firstNameIndex === -1 || lastNameIndex === -1 || emailIndex === -1) {
        showError('CSV must contain columns: first_name (or First Name), last_name (or Last Name), email');
        return;
      }

      const parsedUsers: UserData[] = lines.slice(1)
        .map(line => {
          const values = parseCSVLine(line);

          const additionalData: { [key: string]: string } = {};
          headers.forEach((header, idx) => {
            if (idx !== firstNameIndex && idx !== lastNameIndex && idx !== emailIndex && values[idx]?.trim()) {
              additionalData[header] = values[idx];
            }
          });

          return {
            firstName: values[firstNameIndex] || '',
            lastName: values[lastNameIndex] || '',
            email: values[emailIndex] || '',
            additionalData: Object.keys(additionalData).length > 0 ? additionalData : undefined
          };
        })
        .filter(user => user.firstName || user.lastName || user.email);

      const validatedUsers = validateUserData(parsedUsers);
      setUploadedFile(file);

      const validUsers = validatedUsers.filter(user => user.status === 'valid');

      if (validUsers.length > 0) {
        // Large file confirmation for 50+ users
        if (validUsers.length >= LARGE_ROW_THRESHOLD) {
          setUserData(validatedUsers);
          setShowLargeFileConfirm(true);
          setProcessingPhase('idle');
          processingFileRef.current = null;
          return;
        }

        await processValidUsersWithAI(validUsers, validatedUsers);
      } else {
        setUserData(validatedUsers);
        notificationService.warning('Your CSV has some validation errors. Please review and fix the data.');
      }
    } catch (error) {
      showError('We couldn\'t read your file. Please make sure it\'s a valid CSV file and try again.');
    } finally {
      setProcessingPhase('idle');
      processingFileRef.current = null;
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle confirmation for large file processing
  const handleConfirmLargeFile = async () => {
    setShowLargeFileConfirm(false);
    const validUsers = userData.filter(u => u.status === 'valid');
    if (validUsers.length === 0) return;

    try {
      await processValidUsersWithAI(validUsers, userData);
    } finally {
      setProcessingPhase('idle');
    }
  };

  // Remove user from list
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const removeUser = (index: number) => {
    setUserData(prev => prev.filter((_, i) => i !== index));
  };

  // Reset all data
  const handleReset = () => {
    setUserData([]);
    setProcessedProfiles([]);
    setUploadedFile(null);
    setUploadError('');
    setProcessingPhase('idle');
    setElapsedSeconds(0);
    setCreationResults(null);
    setShowLargeFileConfirm(false);
    cancelledRef.current = false;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Retry AI processing
  const handleRetryAIProcessing = async () => {
    if (userData.length === 0) {
      notificationService.error('No data to process. Please upload a CSV file first.');
      return;
    }

    const validUsers = userData.filter(u => u.status === 'valid');
    if (validUsers.length === 0) {
      notificationService.error('No valid users to process. Please fix validation errors first.');
      return;
    }

    setUploadError('');

    try {
      await processValidUsersWithAI(validUsers, userData);
    } finally {
      setProcessingPhase('idle');
    }
  };

  const handleCreateUsers = async () => {
    if (processedProfiles.length === 0) {
      notificationService.error('Please upload and process a CSV file first.');
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmAndProceed = async () => {
    if (!confirmationChecked) {
      notificationService.warning('Please confirm that you have reviewed the user information.');
      return;
    }

    setShowConfirmDialog(false);
    setConfirmationChecked(false);
    setProcessingPhase('creating');

    let creationNotificationId: string | undefined;
    cancelledRef.current = false;

    try {
      // Split profiles into chunks for processing
      const chunks: any[][] = [];
      for (let i = 0; i < processedProfiles.length; i += CHUNK_SIZE) {
        chunks.push(processedProfiles.slice(i, i + CHUNK_SIZE));
      }

      const totalProfiles = processedProfiles.length;
      let completedCount = 0;
      const allResults: any[] = [];

      creationNotificationId = notificationService.info(`Creating users... 0/${totalProfiles}`, 0);

      const processChunk = async (chunk: any[], chunkIndex: number) => {
        // Check payload size for this chunk
        const payloadSize = estimatePayloadSize({ profiles: chunk, tenant_id: currentTenantId || null });
        if (payloadSize > MAX_PAYLOAD_BYTES) {
          chunk.forEach((p: any) => allResults.push({ email: p.email, success: false, error: 'Payload too large' }));
          return;
        }

        try {
          const result = await batchCreateUsers(chunk, currentTenantId || undefined);

          if (result.success && result.data) {
            const chunkResults = result.data.results || [];
            allResults.push(...chunkResults);
          } else {
            // Chunk-level failure — map backend error code to a user-friendly message
            const details = result.details || '';
            const friendlyError = details === 'failed-precondition'
              ? 'Too many users in a single batch'
              : details === 'unauthenticated'
              ? 'You are not logged in. Please log in and try again.'
              : details === 'permission-denied'
              ? 'You don\'t have permission to create users.'
              : result.error || `Batch ${chunkIndex + 1} failed`;

            chunk.forEach((p: any) => allResults.push({ email: p.email, success: false, error: friendlyError }));
          }
        } catch (chunkError: any) {
          // Network error on this chunk — mark profiles as failed and continue with remaining chunks
          const msg = chunkError.message || '';
          const networkError = msg.includes('Failed to fetch') || msg.includes('network') || chunkError instanceof TypeError
            ? 'Network error — please check your connection'
            : msg || `Batch ${chunkIndex + 1} failed`;
          chunk.forEach((p: any) => allResults.push({ email: p.email, success: false, error: networkError }));
        }

        completedCount += chunk.length;
        if (creationNotificationId) {
          notificationService.update(creationNotificationId, `Creating users... ${completedCount}/${totalProfiles}`);
        }
      };

      // Process chunks in pairs of CHUNK_CONCURRENCY, checking for cancel between batches
      for (let i = 0; i < chunks.length; i += CHUNK_CONCURRENCY) {
        if (cancelledRef.current) {
          for (let j = i; j < chunks.length; j++) {
            chunks[j].forEach((p: any) => allResults.push({ email: p.email, success: false, error: 'Cancelled by user' }));
          }
          break;
        }

        const batch = chunks.slice(i, i + CHUNK_CONCURRENCY);
        await Promise.all(batch.map((chunk, offset) => processChunk(chunk, i + offset)));
      }

      if (creationNotificationId) notificationService.remove(creationNotificationId);

      if (cancelledRef.current) {
        notificationService.info('User creation was cancelled.');
        return;
      }

      // Aggregate results across all chunks
      const newUsers = allResults.filter((r: any) => r.success && !r.isExistingUser);
      const existingUsers = allResults.filter((r: any) => r.success && r.isExistingUser);
      const failedUsers = allResults.filter((r: any) => !r.success);

      const actualFailed = failedUsers.length;
      const actualSuccessful = newUsers.length;
      const alreadyExisted = existingUsers.length;

      setCreationResults({
        successful: newUsers,
        existing: existingUsers,
        failed: failedUsers
      });

      if (actualFailed > 0 && actualSuccessful === 0 && alreadyExisted === 0) {
        // All failed - show error dialog
        setShowCreationErrorDialog(true);
      } else if (actualFailed > 0 || alreadyExisted > 0) {
        // Partial success - show warning dialog
        setShowCreationErrorDialog(true);
      } else {
        // All successful
        notificationService.success(`Successfully created ${actualSuccessful} user${actualSuccessful !== 1 ? 's' : ''}!`);
        handleReset();
      }
    } catch (error: any) {
      if (creationNotificationId) notificationService.remove(creationNotificationId);

      const code = error.code || '';
      const msg = error.message || '';

      const errorMessage = error instanceof TypeError || msg.includes('Failed to fetch') || msg.includes('network')
        ? 'Network error. Please check your connection and try again.'
        : code === 'deadline-exceeded' || code === 'functions/deadline-exceeded'
        ? 'The request took too long. Please check your internet connection and try again.'
        : code === 'functions/unauthenticated'
        ? 'You are not logged in. Please log in and try again.'
        : code === 'functions/permission-denied'
        ? 'You don\'t have permission to create users. Please contact your administrator.'
        : code === 'functions/resource-exhausted'
        ? 'We\'ve reached our account creation limit. Please try again in a few minutes.'
        : code === 'functions/invalid-argument'
        ? 'There\'s an issue with the user data. Please review your file and try again.'
        : code === 'functions/internal'
        ? 'Something went wrong on our end. Please try again.'
        : 'We couldn\'t create the user accounts. Please try again.';

      setCreationError(errorMessage);
      setCreationResults(null);
      setShowCreationErrorDialog(true);
    } finally {
      setProcessingPhase('idle');
    }
  };

  const handleCloseCreationDialog = () => {
    setShowCreationErrorDialog(false);
    setCreationError('');

    if (creationResults) {
      const hadFailures = creationResults.failed.length > 0;
      const hadSuccesses = creationResults.successful.length > 0 || creationResults.existing.length > 0;

      if (hadFailures && hadSuccesses) {
        // Partial success: keep only failed profiles for retry
        const failedEmails = new Set(creationResults.failed.map((u: any) => u.email));
        setProcessedProfiles(prev => prev.filter(p => failedEmails.has(p.email)));
        setCreationResults(null);
        notificationService.info(`${creationResults.failed.length} failed profile(s) kept for retry.`);
      } else if (!hadFailures) {
        // All succeeded or already existed — full reset
        setCreationResults(null);
        handleReset();
      } else {
        // All failed — keep everything for retry
        setCreationResults(null);
      }
    } else {
      // Error without results — keep profiles for retry
      setCreationResults(null);
    }
  };

  return (
    <div className="mt-3 h-full w-full">
      <ConfirmationModal
        isOpen={showErrorDialog}
        onClose={() => {
          setShowErrorDialog(false);
          setUploadError('');
        }}
        title="Upload Error"
        message={uploadError}
        icon={<MdError className="h-12 w-12 text-red-500" />}
        variant="error"
        confirmText="Close"
      />

      <ConfirmationModal
        isOpen={showLargeFileConfirm}
        onClose={() => {
          setShowLargeFileConfirm(false);
        }}
        title="Large File Detected"
        message={`Your CSV has ${userData.filter(u => u.status === 'valid').length} valid users. AI processing may take several minutes. Do you want to continue?`}
        icon={<MdWarning className="h-12 w-12 text-yellow-500" />}
        variant="warning"
        confirmText="Continue Processing"
        showCancel={true}
        cancelText="Cancel"
        onConfirm={handleConfirmLargeFile}
      />

      {showCreationErrorDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              handleCloseCreationDialog();
            }}
          />

          <div className="relative bg-white dark:bg-navy-800 rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className={`flex h-12 w-12 items-center justify-center rounded-full ${
                  creationResults && creationResults.failed.length > 0
                    ? 'bg-yellow-100 dark:bg-yellow-900/30'
                    : 'bg-red-100 dark:bg-red-900/30'
                }`}>
                  <MdWarning className={`text-2xl ${
                    creationResults && creationResults.failed.length > 0
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-red-600 dark:text-red-400'
                  }`} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                    User Creation Results
                  </h2>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {creationResults 
                      ? `${creationResults.successful.length} created, ${creationResults.existing.length} already existed, ${creationResults.failed.length} failed`
                      : 'Operation completed with issues'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowCreationErrorDialog(false);
                  setCreationError('');
                  setCreationResults(null);
                  if (creationResults && creationResults.successful.length > 0) {
                    handleReset();
                  }
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-lg transition-colors"
              >
                <MdClose className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Summary Cards */}
              {creationResults && (
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MdCheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <h3 className="font-semibold text-green-800 dark:text-green-300">Created</h3>
                    </div>
                    <p className="text-3xl font-bold text-green-900 dark:text-green-200">{creationResults.successful.length}</p>
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MdCheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      <h3 className="font-semibold text-blue-800 dark:text-blue-300">Already Existed</h3>
                    </div>
                    <p className="text-3xl font-bold text-blue-900 dark:text-blue-200">{creationResults.existing.length}</p>
                  </div>

                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MdError className="h-5 w-5 text-red-600 dark:text-red-400" />
                      <h3 className="font-semibold text-red-800 dark:text-red-300">Failed</h3>
                    </div>
                    <p className="text-3xl font-bold text-red-900 dark:text-red-200">{creationResults.failed.length}</p>
                  </div>
                </div>
              )}

              {creationError && !creationResults && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm font-medium text-red-900 dark:text-red-100">{creationError}</p>
                </div>
              )}

              {/* Detailed Results Tables */}
              {creationResults && (
                <>
                  {creationResults.successful.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-3 flex items-center gap-2">
                        <MdCheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                        Successfully Created Users
                      </h3>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-navy-900">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Email</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-navy-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {creationResults.successful.map((user: any, index: number) => (
                                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-navy-900">
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{processedProfiles.find((profile) => profile.email === user.email)?.firstName || user.firstName || 'Unknown'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{user.email}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {creationResults.existing.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-3 flex items-center gap-2">
                        <MdCheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        Users Already Existed
                      </h3>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-navy-900">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Email</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-navy-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {creationResults.existing.map((user: any, index: number) => (
                                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-navy-900">
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{processedProfiles.find((profile) => profile.email === user.email)?.firstName || user.firstName || 'Unknown'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{user.email}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {creationResults.failed.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-3 flex items-center gap-2">
                        <MdError className="h-5 w-5 text-red-600 dark:text-red-400" />
                        Failed Users
                      </h3>
                      
                      {/* Group errors */}
                      {(() => {
                        const errorGroups = creationResults.failed.reduce((acc: any, user: any) => {
                          const error = user.error || 'Unknown error';
                          if (!acc[error]) {
                            acc[error] = [];
                          }
                          acc[error].push(user);
                          return acc;
                        }, {});

                        return Object.entries(errorGroups).map(([error, users]: [string, any], groupIndex: number) => (
                          <div key={groupIndex} className="mb-4 last:mb-0">
                            <div className="mb-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                              <p className="text-sm font-semibold text-red-900 dark:text-red-100">{error}</p>
                              <p className="text-xs text-red-700 dark:text-red-300 mt-1">{users.length} user{users.length !== 1 ? 's' : ''} affected</p>
                            </div>
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead className="bg-gray-50 dark:bg-navy-900">
                                    <tr>
                                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Email</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white dark:bg-navy-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {users.map((user: any, index: number) => (
                                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-navy-900">
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{processedProfiles.find((profile) => profile.email === user.email)?.firstName || user.firstName || 'Unknown'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{user.email}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowCreationErrorDialog(false);
                  setCreationError('');
                  setCreationResults(null);
                  if (creationResults && creationResults.successful.length > 0) {
                    handleReset();
                  }
                }}
                className="px-6 py-2 rounded-lg text-sm font-medium bg-brand-500 text-white hover:bg-brand-600 transition-colors shadow-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowConfirmDialog(false);
              setConfirmationChecked(false);
            }}
          />

          <div className="relative bg-white dark:bg-navy-800 rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                  <MdWarning className="text-yellow-600 dark:text-yellow-400 text-2xl" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                    Confirm User Creation
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Please review before proceeding
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setConfirmationChecked(false);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-navy-700 rounded-lg transition-colors"
              >
                <MdClose className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              <div className="mb-6 bg-gray-50 dark:bg-navy-900 rounded-lg p-4">
                <h4 className="font-semibold text-navy-700 dark:text-white mb-3">Summary</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Users</p>
                    <p className="text-2xl font-bold text-navy-700 dark:text-white">{processedProfiles.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Mentors</p>
                    <p className="text-2xl font-bold text-brand-500">{processedProfiles.filter(p => p.mentor).length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Mentees</p>
                    <p className="text-2xl font-bold text-blue-500">{processedProfiles.filter(p => !p.mentor).length}</p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-navy-700 dark:text-white mb-3">Users to be Created</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                  {processedProfiles.map((profile, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-navy-900 rounded text-sm">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-navy-700 dark:text-white">
                          {profile.firstName} {profile.lastName}
                        </span>
                        <span className="mx-2 text-gray-400">•</span>
                        <span className="text-gray-600 dark:text-gray-400 truncate">{profile.email}</span>
                      </div>
                      <span className={`flex-shrink-0 ml-2 px-2 py-1 rounded text-xs font-medium ${
                        profile.mentor
                          ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400'
                          : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      }`}>
                        {profile.mentor ? 'Mentor' : 'Mentee'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 dark:text-yellow-400 mb-2 flex items-center gap-2">
                  <MdWarning className="h-5 w-5" />
                  Important Information
                </h4>
                <ul className="text-sm text-yellow-700 dark:text-yellow-400 space-y-1 ml-7 list-disc">
                  <li>Users will be created in the database</li>
                  {currentTenantId && <li>Users will be added to the current workspace</li>}
                  <li>User roles (Mentor/Mentee) have been assigned by AI</li>
                </ul>
              </div>

              <label className="flex items-start gap-3 p-4 border-2 border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-900 transition-colors">
                <input
                  type="checkbox"
                  checked={confirmationChecked}
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-brand-500 focus:ring-brand-500 cursor-pointer"
                />
                <span className="text-sm font-medium text-navy-700 dark:text-white">
                  I have reviewed the AI-enhanced user profiles and confirm that I agree with the AI-assigned roles and information to create these {processedProfiles.length} user account{processedProfiles.length !== 1 ? 's' : ''}.
                </span>
              </label>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setConfirmationChecked(false);
                }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAndProceed}
                disabled={!confirmationChecked}
                className={`px-6 py-2 rounded-lg text-sm font-medium shadow-lg transition-all ${
                  confirmationChecked
                    ? 'bg-brand-500 text-white hover:bg-brand-600 hover:shadow-xl'
                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                Confirm & Create Users
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 md:h-[390px]" />
      <div className="mx-auto h-full w-11/12 lg:w-10/12 xl:w-9/12 md:px-3">
        <div className="-mt-[280px] w-full pb-10 md:-mt-[240px] flex gap-6">
          {/* Main Content */}
          <div className="flex-1">
            <Card extra={'h-full pb-3 shadow-lg'}>
              <div className="rounded-[20px] px-3 pt-7 md:px-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-500/10">
                    <MdUploadFile className="text-brand-500 text-2xl" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                      Add Users via File
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Upload a CSV file to create multiple user accounts
                    </p>
                  </div>
                </div>

                {/* File Upload Section */}
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-navy-700 dark:text-white">
                        Upload CSV File
                      </label>
                      <button
                        onClick={downloadTemplate}
                        className="flex items-center gap-2 text-sm text-brand-500 hover:text-brand-600"
                      >
                        <MdDownload className="h-4 w-4" />
                        Download Template
                      </button>
                    </div>
                    
                    <div
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        if (file) {
                          handleFileUpload(file);
                        }
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                        isProcessing || isCreatingUsers
                          ? 'border-gray-200 dark:border-gray-700 opacity-50 pointer-events-none'
                          : 'border-gray-300 dark:border-gray-600 hover:border-brand-500'
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(file);
                          if (e.target) e.target.value = '';
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing || isCreatingUsers}
                      />
                      <MdUploadFile className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                      <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">
                        {uploadedFile ? uploadedFile.name : "Drop CSV file here or click to browse"}
                      </p>
                      <p className="text-sm text-gray-500">
                        Required: first_name, last_name, email
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        Please modify your CSV similar to our template for more accurate processing - Latte AI transforms all columns into structured profiles
                      </p>
                    </div>
                  </div>

                  {/* Processing Indicator */}
                  {(isProcessing || isCreatingUsers) && (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        {processingPhase === 'parsing' && 'Parsing CSV file...'}
                        {processingPhase === 'ai-processing' && `Processing with Latte AI... ${formatElapsed(elapsedSeconds)}`}
                        {processingPhase === 'creating' && `Creating user accounts... ${formatElapsed(elapsedSeconds)}`}
                      </p>
                      {processingPhase === 'ai-processing' && (
                        <button
                          onClick={handleCancelProcessing}
                          className="mt-3 px-4 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  )}

                  {/* Retry AI Processing */}
                  {!isProcessing && !isCreatingUsers && userData.length > 0 && userData.some(u => u.status === 'valid') && processedProfiles.length === 0 && (
                    <div className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                          AI processing did not complete successfully.
                        </p>
                        <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                          {userData.filter(u => u.status === 'valid').length} valid user(s) ready to retry.
                        </p>
                      </div>
                      <button
                        onClick={handleRetryAIProcessing}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                      >
                        <MdRefresh className="h-4 w-4" />
                        Retry
                      </button>
                    </div>
                  )}

                  {/* AI Enhanced Profiles - Card View */}
                  {processedProfiles.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h5 className="text-lg font-semibold text-navy-700 dark:text-white">
                          AI Enhanced Profiles ({processedProfiles.length})
                        </h5>
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1 text-sm text-blue-600">
                            <MdCheckCircle className="h-4 w-4" />
                            Ready to create
                          </span>
                          <button
                            onClick={handleReset}
                            disabled={isProcessing || isCreatingUsers}
                            className="linear flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 transition duration-200 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <MdDelete className="h-3 w-3" />
                            Reset
                          </button>
                        </div>
                      </div>

                      <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg mb-6">
                        <div className="space-y-2 p-4">
                          {processedProfiles.map((profile, index) => {
                            const additionalData: { [key: string]: string } = {};

                            if (profile.quote) additionalData['Quote'] = profile.quote;
                            if (profile.professional !== undefined) additionalData['Professional'] = profile.professional ? 'Yes' : 'No';
                            if (profile.mentor !== undefined) additionalData['Mentor'] = profile.mentor ? 'Yes' : 'No';

                            if (profile.locationData) {
                              additionalData['Location'] = `${profile.locationData.latitude}, ${profile.locationData.longitude}`;
                            }

                            if (profile.educations.length > 0) {
                              profile.educations.forEach((edu: Education, i: number) => {
                                const prefix = profile.educations.length > 1 ? `Education ${i + 1}` : 'Education';
                                additionalData[`${prefix} - College`] = edu.collegeName;
                                additionalData[`${prefix} - Level`] = edu.level;
                                if (edu.majors.length > 0) additionalData[`${prefix} - Majors`] = edu.majors.join(', ');
                                if (edu.startTime) additionalData[`${prefix} - Start`] = String(edu.startTime);
                                if (edu.endTime) additionalData[`${prefix} - End`] = String(edu.endTime);
                                if (edu.description) additionalData[`${prefix} - Description`] = edu.description;
                              });
                            }

                            if (profile.works.length > 0) {
                              profile.works.forEach((work: Work, i: number) => {
                                const prefix = profile.works.length > 1 ? `Work ${i + 1}` : 'Work';
                                additionalData[`${prefix} - Company`] = work.companyName;
                                additionalData[`${prefix} - Position`] = work.position;
                                if (work.field.length > 0) additionalData[`${prefix} - Field`] = work.field.join(', ');
                                if (work.startTime) additionalData[`${prefix} - Start`] = String(work.startTime);
                                if (work.endTime) additionalData[`${prefix} - End`] = String(work.endTime);
                                if (work.description) additionalData[`${prefix} - Description`] = work.description;
                              });
                            }

                            if (profile.activities.length > 0) {
                              profile.activities.forEach((activity: Activity, i: number) => {
                                const prefix = profile.activities.length > 1 ? `Activity ${i + 1}` : 'Activity';
                                additionalData[`${prefix} - Organization`] = activity.organization;
                                additionalData[`${prefix} - Position`] = activity.position;
                                if (activity.startTime) additionalData[`${prefix} - Start`] = String(activity.startTime);
                                if (activity.endTime) additionalData[`${prefix} - End`] = String(activity.endTime);
                                if (activity.description) additionalData[`${prefix} - Description`] = activity.description;
                              });
                            }

                            if (profile.questionResponses.length > 0) {
                              profile.questionResponses.forEach((qr: QuestionResponse, i: number) => {
                                additionalData[qr.question || `Question ${i + 1}`] = qr.response;
                              });
                            }

                            if (profile.interests.length > 0) {
                              additionalData['Interests'] = profile.interests.join(', ');
                            }

                            if (profile.links.length > 0) {
                              additionalData['Links'] = profile.links.join(', ');
                            }

                            return (
                              <UserCard
                                key={index}
                                user={{
                                  firstName: profile.firstName,
                                  lastName: profile.lastName,
                                  email: profile.email,
                                  additionalData: Object.keys(additionalData).length > 0 ? additionalData : undefined,
                                  status: 'valid'
                                }}
                                index={index}
                                isExpanded={expandedUsers.has(index)}
                                onToggle={() => toggleUserExpand(index)}
                                onRemove={() => {
                                  setProcessedProfiles(prev => prev.filter((_, i) => i !== index));
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>

                      {/* Confirm Button */}
                      <div className="flex justify-end mb-6">
                        <button
                          onClick={handleCreateUsers}
                          disabled={isCreatingUsers}
                          className={`linear flex items-center gap-2 rounded-xl px-4 py-2 font-medium transition duration-200 ${
                            isCreatingUsers
                              ? "bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                              : "bg-brand-400 text-white hover:bg-brand-500 active:bg-brand-600 dark:bg-brand-300 dark:hover:bg-brand-400 dark:active:bg-brand-500"
                          }`}
                        >
                          <MdCheckCircle className="h-4 w-4" />
                          {isCreatingUsers
                            ? "Creating Users..."
                            : `Confirm & Add ${processedProfiles.length} User${processedProfiles.length !== 1 ? 's' : ''}`}
                        </button>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </Card>
          </div>
          
          {/* Floating Information Panel */}
          <div className="hidden xl:block w-80">
            <div className="sticky top-6">
              <Card extra="p-6 shadow-lg">
              <div>
                <h6 className="font-semibold text-navy-700 dark:text-white mb-2">
                  Bulk User Creation
                </h6>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  Upload a CSV file to create multiple user accounts. Latte AI automatically processes and enhances 
                  the data to create rich profiles with structured information for better connections.
                </p>
                
                <div className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  {['Download the CSV template', 'Fill in user information', 'Upload file - Latte AI processes automatically', 'Review and create user accounts'].map((step, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs text-brand-500 font-bold">{i + 1}.</span>
                      <span>{step}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h6 className="text-xs font-semibold text-navy-700 dark:text-white mb-2">Required Columns:</h6>
                  <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                    {['first_name (or First Name)', 'last_name (or Last Name)', 'email'].map((col, i) => (
                      <li key={i}>• {col}</li>
                    ))}
                  </ul>
                </div>

                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <h6 className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-2">
                    Custom Data Columns:
                  </h6>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mb-2 leading-relaxed">
                    Add or remove columns for any user information. Latte AI intelligently processes diverse data types to build meaningful connections.
                  </p>
                  <p className="text-xs text-blue-500 dark:text-blue-500 italic">
                    Examples: Education, Work Experience, Activities, Interests, Location, Personal Quote, Links, Custom Questions
                  </p>
                </div>

                <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <h6 className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 mb-1">File Requirements:</h6>
                  <ul className="text-xs text-yellow-600 dark:text-yellow-400 space-y-1">
                    {['CSV format only', 'No duplicate email addresses', 'Each line should represent one user'].map((req, i) => (
                      <li key={i}>• {req}</li>
                    ))}
                  </ul>
                </div>
              </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUserFile;